import React, { useState, useEffect } from 'react';
import { Fab, Zoom, useTheme } from '@mui/material';
import { KeyboardArrowUp } from '@mui/icons-material';

/**
 * Tasteful scroll to top button that appears when user scrolls down
 * Implements smooth scrolling and elegant animations
 */
const ScrollToTopButton = () => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);

  // Show button when page is scrolled down
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 400) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  // Smooth scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Zoom in={isVisible} timeout={300}>
      <Fab
        onClick={scrollToTop}
        size="medium"
        aria-label="scroll to top"
        sx={{
          position: 'fixed',
          bottom: { xs: 20, sm: 32 },
          right: { xs: 20, sm: 32 },
          zIndex: 1200,
          backgroundColor: theme.palette.accent?.main || '#00695c',
          color: theme.palette.accent?.contrastText || 'white',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: theme.palette.accent?.dark || '#004d40',
            transform: 'translateY(-4px) scale(1.05)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.25)',
          },
          '&:active': {
            transform: 'translateY(-2px) scale(1.02)',
          },
          // Subtle pulse animation
          '@keyframes pulse': {
            '0%': {
              boxShadow: `0 8px 32px rgba(${theme.palette.mode === 'dark' ? '255, 255, 255' : '0, 0, 0'}, 0.15)`,
            },
            '50%': {
              boxShadow: `0 8px 32px rgba(${theme.palette.mode === 'dark' ? '255, 255, 255' : '0, 0, 0'}, 0.25)`,
            },
            '100%': {
              boxShadow: `0 8px 32px rgba(${theme.palette.mode === 'dark' ? '255, 255, 255' : '0, 0, 0'}, 0.15)`,
            },
          },
          animation: 'pulse 3s infinite',
        }}
      >
        <KeyboardArrowUp 
          sx={{ 
            fontSize: 28,
            transition: 'transform 0.2s ease',
          }} 
        />
      </Fab>
    </Zoom>
  );
};

export default ScrollToTopButton;